import React, { FC, createContext, useContext, useMemo, useReducer, useCallback, useRef, useEffect } from 'react';
import { useCopyToCastItPolling } from './useCopyToCastItPolling';
import { useCopyToCastItStudioPolling } from './useCopyToCastItStudioPolling';
import { PollingInstance, PollingInstanceStatus, PollingInstanceType } from './types';

export const POLLING_STORAGE_KEY = 'pollingInstances';

type InstancesByType = Record<
  PollingInstanceType,
  {
    instances: PollingInstance[];
    displayStatus: PollingInstanceStatus;
  }
>;

interface PollingState {
  instances: PollingInstance[];
}

interface PollingContextValue {
  pollingInstances: PollingInstance[];
  instancesByType: Partial<InstancesByType>;
  addPollingInstance: (instance: Omit<PollingInstance, 'status'>) => void;
}

// REDUCER - Handles all state transitions for polling instances
type PollingAction =
  | { type: 'ADD_INSTANCE'; payload: PollingInstance }
  | { type: 'SYNC_POLL_RESULTS'; payload: PollingInstance[] }
  | { type: 'CLEAR_COMPLETED_IF_IDLE' };

const getInstanceKey = ({ id, type }: Pick<PollingInstance, 'id' | 'type'>) => `${type}:${id}`;

const loadPersistedInstances = (): PollingInstance[] => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = localStorage.getItem(POLLING_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const validTypes = new Set(Object.values(PollingInstanceType));
    const validStatuses = new Set(Object.values(PollingInstanceStatus));

    return parsed.filter(
      (item): item is PollingInstance =>
        item && typeof item.id === 'string' && validTypes.has(item.type) && validStatuses.has(item.status)
    );
  } catch {
    return [];
  }
};

const pollingReducer = (state: PollingState, action: PollingAction): PollingState => {
  switch (action.type) {
    case 'ADD_INSTANCE': {
      const exists = state.instances.some((inst) => inst.id === action.payload.id && inst.type === action.payload.type);
      if (exists) return state;

      const newInstances = [...state.instances, action.payload];
      localStorage.setItem(POLLING_STORAGE_KEY, JSON.stringify(newInstances));

      return { instances: newInstances };
    }

    case 'SYNC_POLL_RESULTS': {
      const updatesByKey = new Map(action.payload.map((instance) => [getInstanceKey(instance), instance]));

      const mergedInstances = state.instances.map((instance) => {
        const updated = updatesByKey.get(getInstanceKey(instance));
        return updated ?? instance;
      });

      const prunedInstances = mergedInstances.filter(
        (instance) =>
          instance.status === PollingInstanceStatus.IN_PROGRESS || updatesByKey.has(getInstanceKey(instance))
      );

      localStorage.setItem(POLLING_STORAGE_KEY, JSON.stringify(prunedInstances));

      return {
        instances: prunedInstances,
      };
    }

    case 'CLEAR_COMPLETED_IF_IDLE': {
      const hasInProgress = state.instances.some((instance) => instance.status === PollingInstanceStatus.IN_PROGRESS);
      if (hasInProgress) {
        return state;
      }
      localStorage.removeItem(POLLING_STORAGE_KEY);
      return { instances: [] };
    }

    default:
      return state;
  }
};

// CONTEXT
const PollingReactContext = createContext<PollingContextValue>({
  pollingInstances: [],
  instancesByType: {},
  addPollingInstance: () => {
    console.error('PollingProvider not initialized: addPollingInstance called outside provider');
  },
});

/**
 * Groups polling instances by type and determines the display status for each group.
 * Display status priority: failed > partial-success > completed > in-progress
 */
const computeInstancesByType = (instances: PollingInstance[]): Partial<InstancesByType> => {
  return instances.reduce<Partial<InstancesByType>>((acc, instance) => {
    if (!acc[instance.type]) {
      acc[instance.type] = {
        instances: [instance],
        displayStatus: instance.status,
      };
    } else {
      acc[instance.type].instances.push(instance);

      if (instance.status === PollingInstanceStatus.FAILED) {
        acc[instance.type].displayStatus = PollingInstanceStatus.FAILED;
      } else if (
        instance.status === PollingInstanceStatus.PARTIAL_SUCCESS &&
        acc[instance.type].displayStatus !== PollingInstanceStatus.FAILED
      ) {
        acc[instance.type].displayStatus = PollingInstanceStatus.PARTIAL_SUCCESS;
      } else if (
        instance.status === PollingInstanceStatus.COMPLETED &&
        acc[instance.type].displayStatus !== PollingInstanceStatus.FAILED &&
        acc[instance.type].displayStatus !== PollingInstanceStatus.PARTIAL_SUCCESS
      ) {
        acc[instance.type].displayStatus = PollingInstanceStatus.COMPLETED;
      } else if (
        instance.status === PollingInstanceStatus.IN_PROGRESS &&
        acc[instance.type].displayStatus !== PollingInstanceStatus.FAILED &&
        acc[instance.type].displayStatus !== PollingInstanceStatus.PARTIAL_SUCCESS &&
        acc[instance.type].displayStatus !== PollingInstanceStatus.COMPLETED
      ) {
        acc[instance.type].displayStatus = PollingInstanceStatus.IN_PROGRESS;
      }
    }

    return acc;
  }, {});
};

export const PollingProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(pollingReducer, { instances: loadPersistedInstances() });

  const { pollCopyToCastIt } = useCopyToCastItPolling();
  const { pollCopyToCastItStudio } = useCopyToCastItStudioPolling();

  const isPollingInProgressRef = useRef(false);

  const doPolling = useCallback(async () => {
    // Prevent overlapping polls - skip if previous poll is still running
    if (isPollingInProgressRef.current) {
      return;
    }

    // filter out completed instances, once polling finishes they are removed from state
    const activeInstances = state.instances.filter((inst) => inst.status === PollingInstanceStatus.IN_PROGRESS);

    if (activeInstances.length === 0) {
      dispatch({ type: 'CLEAR_COMPLETED_IF_IDLE' });
      return;
    }

    isPollingInProgressRef.current = true;
    try {
      const updatedInstances = await Promise.all(
        activeInstances.map((instance) => {
          if (instance.type === PollingInstanceType.COPY_TO_CASTIT) {
            return pollCopyToCastIt({ instance });
          }
          if (instance.type === PollingInstanceType.COPY_TO_CASTIT_STUDIO) {
            return pollCopyToCastItStudio({ instance });
          }

          return { ...instance, status: PollingInstanceStatus.FAILED };
        })
      );

      dispatch({ type: 'SYNC_POLL_RESULTS', payload: updatedInstances });
    } finally {
      isPollingInProgressRef.current = false;
    }
  }, [state.instances, pollCopyToCastIt, pollCopyToCastItStudio]);
  const pollingHandlerRef = useRef(doPolling);

  const addPollingInstance = useCallback((instance: Omit<PollingInstance, 'status'>) => {
    dispatch({
      type: 'ADD_INSTANCE',
      payload: { ...instance, status: PollingInstanceStatus.IN_PROGRESS },
    });
  }, []);

  // update the polling handler ref whenever it changes
  useEffect(() => {
    pollingHandlerRef.current = doPolling;
  }, [doPolling]);

  // start the interval to poll every 5 seconds on mount
  // clear it on unmount
  useEffect(() => {
    const id = setInterval(() => pollingHandlerRef?.current?.(), 5000);

    return () => {
      clearInterval(id);
    };
  }, []);

  const contextValue = useMemo<PollingContextValue>(
    () => ({
      pollingInstances: state.instances,
      instancesByType: computeInstancesByType(state.instances),
      addPollingInstance,
    }),
    [state.instances, addPollingInstance]
  );

  return <PollingReactContext.Provider value={contextValue}>{children}</PollingReactContext.Provider>;
};

export const usePollingContext = (): PollingContextValue => {
  return useContext(PollingReactContext);
};
